import React, { useState } from 'react';
import {
  Box,
  VStack,
  Card,
  CardHeader,
  CardBody,
  Text,
  SimpleGrid,
  useColorModeValue,
  Heading,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  HStack,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const SubscriptionPlanManagement = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([
    {
      id: 1,
      name: 'Basic',
      price: 19.99,
      features: [
        '2 Chat Bots',
        '1 Voice Bot',
        '5,000 Messages/month',
        '2GB Storage',
        'Email Support'
      ],
      status: 'active'
    },
    {
      id: 2,
      name: 'Professional',
      price: 49.99,
      features: [
        '5 Chat Bots',
        '2 Voice Bots',
        '10,000 Messages/month',
        '5GB Storage',
        'Priority Support'
      ],
      status: 'active'
    },
    {
      id: 3,
      name: 'Enterprise',
      price: 99.99,
      features: [
        'Unlimited Chat Bots',
        'Unlimited Voice Bots',
        'Unlimited Messages',
        '20GB Storage',
        '24/7 Support'
      ],
      status: 'active'
    }
  ]);

  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    features: '',
    status: 'active'
  });

  const toast = useToast();
  const { isAdmin, isSuperAdmin } = useAuth();
  const cardBg = useColorModeValue('white', 'gray.700');

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setSelectedPlan(plan);
      setFormData({
        name: plan.name,
        price: plan.price,
        features: plan.features.join('\n'),
        status: plan.status
      });
    } else {
      setSelectedPlan(null);
      setFormData({
        name: '',
        price: 0,
        features: '',
        status: 'active'
      });
    }
    onOpen();
  };

  const handleCloseModal = () => {
    setSelectedPlan(null);
    setFormData({
      name: '',
      price: 0,
      features: '',
      status: 'active'
    });
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const features = formData.features.split('\n').filter(f => f.trim());

    if (selectedPlan) {
      // Update existing plan
      setPlans(plans.map(plan =>
        plan.id === selectedPlan.id
          ? { ...plan, ...formData, features }
          : plan
      ));
      toast({
        title: 'Plan Updated',
        description: 'The subscription plan has been updated successfully.',
        status: 'success',
        duration: 3000,
      });
    } else {
      // Create new plan
      const newPlan = {
        id: plans.length + 1,
        ...formData,
        features,
      };
      setPlans([...plans, newPlan]);
      toast({
        title: 'Plan Created',
        description: 'A new subscription plan has been created successfully.',
        status: 'success',
        duration: 3000,
      });
    }
    handleCloseModal();
  };

  const handleDelete = (planId) => {
    setPlans(plans.filter(plan => plan.id !== planId));
    toast({
      title: 'Plan Deleted',
      description: 'The subscription plan has been deleted successfully.',
      status: 'success',
      duration: 3000,
    });
  };

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.800')} p={6}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="xl">Subscription Plan Management</Heading>
          {(isAdmin() || isSuperAdmin()) && (
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={() => handleOpenModal()}
            >
              Add New Plan
            </Button>
          )}
        </HStack>

        <Card bg={cardBg} boxShadow="md">
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Plan Name</Th>
                  <Th>Price</Th>
                  <Th>Features</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {plans.map((plan) => (
                  <Tr key={plan.id}>
                    <Td>{plan.name}</Td>
                    <Td>${plan.price}/month</Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        {plan.features.map((feature, index) => (
                          <Text key={index} fontSize="sm">• {feature}</Text>
                        ))}
                      </VStack>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={plan.status === 'active' ? 'green' : 'red'}
                      >
                        {plan.status}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<FiEdit2 />}
                          size="sm"
                          onClick={() => handleOpenModal(plan)}
                          isDisabled={!isAdmin() && !isSuperAdmin()}
                        />
                        <IconButton
                          icon={<FiTrash2 />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => handleDelete(plan.id)}
                          isDisabled={!isAdmin() && !isSuperAdmin()}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>

        {/* Add/Edit Plan Modal */}
        <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {selectedPlan ? 'Edit Subscription Plan' : 'Add New Subscription Plan'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Plan Name</FormLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter plan name"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Price (per month)</FormLabel>
                    <NumberInput
                      min={0}
                      precision={2}
                      value={formData.price}
                      onChange={(value) => setFormData({ ...formData, price: parseFloat(value) })}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Features (one per line)</FormLabel>
                    <Textarea
                      value={formData.features}
                      onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                      placeholder="Enter features, one per line"
                      rows={5}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Status</FormLabel>
                    <Input
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      placeholder="active or inactive"
                    />
                  </FormControl>

                  <Button type="submit" colorScheme="blue" width="full">
                    {selectedPlan ? 'Update Plan' : 'Create Plan'}
                  </Button>
                </VStack>
              </form>
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default SubscriptionPlanManagement; 